import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaFacebookF, 
  FaInstagram, 
  FaYoutube,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaWhatsapp
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import logo from '../assets/imges/SKY MUSE LOGO (2).png';
import { useWebsiteData } from '../context/WebsiteDataContext';

const Footer = () => {
  const { contact } = useWebsiteData();

  // WhatsApp configuration
  const whatsappNumber = contact?.whatsapp || '918700186979';
  const whatsappMessage = 'Hello! I would like to enquire about your digital marketing services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  // Page Links
  const pageLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Process', href: '/process' },
    { name: 'Industries', href: '/industries' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Contact', href: '/contact' },
  ];

  const whatsappLink = contact?.socialLinks?.whatsapp || '#';
  const instagramLink = contact?.socialLinks?.instagram || 'https://www.instagram.com/skymuse.in';
  const facebookLink = contact?.socialLinks?.facebook || 'https://m.facebook.com/skymuse.in';
  const youtubeLink = contact?.socialLinks?.youtube || 'https://m.youtube.com/@SkyMusedotin';
  const twitterLink = contact?.socialLinks?.twitter || 'https://x.com/skymusedotin';

  const socialLinks = [
    { icon: FaWhatsapp, href: '#', color: 'hover:bg-green-500', onClick: openWhatsApp },
    { icon: FaInstagram, href: instagramLink, color: 'hover:bg-pink-600' },
    { icon: FaFacebookF, href: facebookLink, color: 'hover:bg-blue-600' },
    { icon: FaYoutube, href: youtubeLink, color: 'hover:bg-red-600' },
    { icon: FaXTwitter, href: twitterLink, color: 'hover:bg-black' },
  ];

  return (
    <footer className="bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-[150px]" />
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 mb-6 group">
                <motion.img 
                  src={logo} 
                  alt="Sky Muse Logo" 
                  className="w-12 h-12 object-contain"
                  whileHover={{ rotate: [0, -5, 5, -5, 0], scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                />
                <span className="text-2xl font-bold text-white">
                  Sky<span className="text-red-500">Muse</span>
                </span>
              </Link>

              <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
                We're a full-service digital marketing agency helping businesses grow 
                their online presence and achieve measurable results through data-driven strategies.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <button onClick={openWhatsApp} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group cursor-pointer bg-transparent border-none text-left">
                  <FaWhatsapp className="text-green-500 group-hover:scale-110 transition-transform text-lg" />
                  <span>{contact?.whatsapp ? `+${contact.whatsapp.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '+$1 $2 $3 $4')}` : '+91 870 018 6979'}</span>
                </button>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(contact?.address || '13, Shivaji Marg, Moti Nagar, Delhi')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group">
                  <FaMapMarkerAlt className="text-red-500 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="text-sm">{contact?.address || '13, Shivaji Marg, Moti Nagar, Block C, Najafgarh Road Industrial Area, Delhi, 110015'}</span>
                </a>
                <a href={`tel:${(contact?.mobile || '+918700186979').replace(/\s/g, '')}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group">
                  <FaPhone className="text-red-500 group-hover:scale-110 transition-transform" />
                  <span>{contact?.mobile || '+91 870 018 6979'}</span>
                </a>
                <a href={`mailto:${contact?.email || 'Info@skymuse.in'}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group">
                  <FaEnvelope className="text-red-500 group-hover:scale-110 transition-transform" />
                  <span>{contact?.email || 'Info@skymuse.in'}</span>
                </a>
              </div>

              {/* Social Links */}
              <div className="flex gap-3 mt-6">
                {socialLinks.map((social, index) => (
                  social.onClick ? (
                    <motion.button
                      key={index}
                      onClick={social.onClick}
                      whileHover={{ y: -3 }}
                      className={`w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white ${social.color} transition-all cursor-pointer`}
                    >
                      <social.icon />
                    </motion.button>
                  ) : (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -3 }}
                      className={`w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white ${social.color} transition-all cursor-pointer`}
                    >
                      <social.icon />
                    </motion.a>
                  )
                ))}
              </div>
            </motion.div>
          </div>

          {/* Quick Links & CTA Column */}
          <div className="flex flex-col justify-between">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {pageLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.href}
                    className="text-gray-400 hover:text-white hover:pl-2 transition-all inline-block py-2"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* WhatsApp CTA Button */}
            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-3 cursor-pointer"
            >
              <FaWhatsapp className="text-2xl" />
              Chat on WhatsApp
            </motion.button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            © {new Date().getFullYear()} SkyMuse. All rights reserved. Made with ❤️ for digital success.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
